import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TodoPersonalSectionResponse } from '@/lib/api';

const NO_PARENT = 'none';

interface EditPersonalSectionDialogProps {
  open: boolean;
  onClose: () => void;
  section: TodoPersonalSectionResponse | null;
  /** All personal sections, for the parent picker. */
  sections: TodoPersonalSectionResponse[];
  onSave: (
    id: string,
    input: { name?: string; description?: string | null; parentPersonalSectionId?: string | null },
  ) => void;
}

/**
 * Edit a private "My Tasks" section — name, description, and parent section.
 * Mirrors the shared list's EditSectionDialog, including the cycle guard that
 * hides the section itself and its descendants from the parent picker.
 */
export function EditPersonalSectionDialog({
  open,
  onClose,
  section,
  sections,
  onSave,
}: EditPersonalSectionDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState<string>(NO_PARENT);

  useEffect(() => {
    if (open && section) {
      setName(section.name);
      setDescription(section.description ?? '');
      setParentId(section.parentPersonalSectionId ?? NO_PARENT);
    }
  }, [open, section]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!section || !name.trim()) return;
    const newParent = parentId === NO_PARENT ? null : parentId;
    const oldParent = section.parentPersonalSectionId ?? null;
    onSave(section.personalSectionId, {
      name: name.trim(),
      description: description.trim() || null,
      ...(newParent !== oldParent ? { parentPersonalSectionId: newParent } : {}),
    });
    onClose();
  }

  // Exclude the section itself and its descendants so a section can't become its
  // own ancestor (would break the tree render).
  const availableParents = (() => {
    if (!section) return [];
    const descendantIds = new Set<string>();
    const collect = (parentId: string) => {
      for (const s of sections) {
        if (s.parentPersonalSectionId === parentId && !descendantIds.has(s.personalSectionId)) {
          descendantIds.add(s.personalSectionId);
          collect(s.personalSectionId);
        }
      }
    };
    collect(section.personalSectionId);
    return sections.filter(
      (s) =>
        s.personalSectionId !== section.personalSectionId &&
        !descendantIds.has(s.personalSectionId),
    );
  })();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Section</DialogTitle>
          <DialogDescription>Update your private section.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-personal-section-name">Name</Label>
            <Input
              id="edit-personal-section-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-personal-section-description">Description (optional)</Label>
            <Textarea
              id="edit-personal-section-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Parent Section</Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger>
                <SelectValue placeholder="None (top-level)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PARENT}>None (top-level)</SelectItem>
                {availableParents.map((s) => (
                  <SelectItem key={s.personalSectionId} value={s.personalSectionId}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
